import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';

const FilterPanel = ({ filters, onFiltersChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localSearch, setLocalSearch] = useState(filters.search);

  const countries = ['Bangladesh', 'India', 'Philippines', 'China', 'Pakistan', 'Sri Lanka', 'Nepal'];
  const skills = ['Construction Worker', 'Electrician', 'Domestic Helper', 'Chef', 'Nurse', 'Driver', 'Mechanic'];
  const vdrTypes = ['VDR', 'eQuotaSpecial'];
  const recruiters = ['Global Recruitment Agency', 'Asia Pacific Recruiters', 'Southeast Manpower', 'International Talent Hub', 'Healthcare Recruitment Co'];

  // Debounce search term - wait 500ms after user stops typing
  useEffect(() => {
    const handler = setTimeout(() => {
      onFiltersChange(prev => ({
        ...prev,
        search: localSearch
      }));
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [localSearch]);

  // Sync local search with external filters
  useEffect(() => {
    setLocalSearch(filters.search);
  }, [filters.search]);

  const handleFilterChange = (key, value) => {
    onFiltersChange(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleDateRangeChange = (type, value) => {
    onFiltersChange(prev => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [type]: value
      }
    }));
  };

  const clearAllFilters = () => {
    setLocalSearch('');
    onFiltersChange({
      search: '',
      country: '',
      skill: '',
      vdrType: '',
      recruiter: '',
      dateRange: { start: '', end: '' }
    });
  };

  const hasActiveFilters = filters.search || filters.country || filters.skill || 
                          filters.vdrType || filters.recruiter || 
                          filters.dateRange.start || filters.dateRange.end;

  return (
    <div className="card">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary">Search & Filter</h2>
          <div className="flex items-center space-x-3">
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="text-sm text-error hover:text-error-600 font-medium"
              >
                Clear All
              </button>
            )}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center space-x-1 text-sm text-primary hover:text-primary-700 font-medium"
            >
              <span>{isExpanded ? 'Less Filters' : 'More Filters'}</span>
              <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
            <input
              type="text"
              placeholder="Search by worker name or passport... (Press Enter or wait 0.5s)"
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onFiltersChange(prev => ({
                    ...prev,
                    search: localSearch
                  }));
                }
              }}
              className="input-field pl-10 w-full"
            />
          </div>
        </div>

        {/* Basic Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Country</label>
            <select
              value={filters.country}
              onChange={(e) => handleFilterChange('country', e.target.value)}
              className="input-field w-full"
            >
              <option value="">All Countries</option>
              {countries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Skill Category</label>
            <select
              value={filters.skill}
              onChange={(e) => handleFilterChange('skill', e.target.value)}
              className="input-field w-full"
            >
              <option value="">All Skills</option>
              {skills.map(skill => (
                <option key={skill} value={skill}>{skill}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">VDR Type</label>
            <select
              value={filters.vdrType}
              onChange={(e) => handleFilterChange('vdrType', e.target.value)}
              className="input-field w-full"
            >
              <option value="">All Types</option>
              {vdrTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Advanced Filters */}
        {isExpanded && (
          <div className="border-t border-secondary-200 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Recruiter</label>
                <select
                  value={filters.recruiter}
                  onChange={(e) => handleFilterChange('recruiter', e.target.value)}
                  className="input-field w-full"
                >
                  <option value="">All Recruiters</option>
                  {recruiters.map(recruiter => (
                    <option key={recruiter} value={recruiter}>{recruiter}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">From Date</label>
                <input
                  type="date"
                  value={filters.dateRange.start}
                  onChange={(e) => handleDateRangeChange('start', e.target.value)}
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">To Date</label>
                <input
                  type="date"
                  value={filters.dateRange.end}
                  onChange={(e) => handleDateRangeChange('end', e.target.value)}
                  className="input-field w-full"
                />
              </div>
            </div>

            {/* Quick Filter Buttons */}
            <div className="mt-4 pt-4 border-t border-secondary-200">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-sm font-medium text-text-secondary">Quick Filters:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleFilterChange('vdrType', 'VDR')}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors duration-150 ${
                    filters.vdrType === 'VDR' ?'bg-primary text-white border-primary' :'bg-surface text-text-secondary border-secondary-300 hover:bg-secondary-50'
                  }`}
                >
                  VDR Only
                </button>
                <button
                  onClick={() => handleFilterChange('vdrType', 'eQuotaSpecial')}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors duration-150 ${
                    filters.vdrType === 'eQuotaSpecial' ?'bg-primary text-white border-primary' :'bg-surface text-text-secondary border-secondary-300 hover:bg-secondary-50'
                  }`}
                >
                  eQuotaSpecial Only
                </button>
                <button
                  onClick={() => handleDateRangeChange('start', new Date().toISOString().split('T')[0])}
                  className="px-3 py-1 text-sm rounded-full border bg-surface text-text-secondary border-secondary-300 hover:bg-secondary-50 transition-colors duration-150"
                >
                  Today's Submissions
                </button>
                <button
                  onClick={() => {
                    const weekAgo = new Date();
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    handleDateRangeChange('start', weekAgo.toISOString().split('T')[0]);
                  }}
                  className="px-3 py-1 text-sm rounded-full border bg-surface text-text-secondary border-secondary-300 hover:bg-secondary-50 transition-colors duration-150"
                >
                  Last 7 Days
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="mt-4 pt-4 border-t border-secondary-200">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-sm font-medium text-text-secondary">Active Filters:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {filters.search && (
                <span className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full">
                  Search: {filters.search}
                  <button
                    onClick={() => handleFilterChange('search', '')}
                    className="ml-1 text-primary-600 hover:text-primary-800"
                  >
                    <Icon name="X" size={12} />
                  </button>
                </span>
              )}
              {filters.country && (
                <span className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full">
                  Country: {filters.country}
                  <button
                    onClick={() => handleFilterChange('country', '')}
                    className="ml-1 text-primary-600 hover:text-primary-800"
                  >
                    <Icon name="X" size={12} />
                  </button>
                </span>
              )}
              {filters.skill && (
                <span className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full">
                  Skill: {filters.skill}
                  <button
                    onClick={() => handleFilterChange('skill', '')}
                    className="ml-1 text-primary-600 hover:text-primary-800"
                  >
                    <Icon name="X" size={12} />
                  </button>
                </span>
              )}
              {filters.vdrType && (
                <span className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full">
                  Type: {filters.vdrType}
                  <button
                    onClick={() => handleFilterChange('vdrType', '')}
                    className="ml-1 text-primary-600 hover:text-primary-800"
                  >
                    <Icon name="X" size={12} />
                  </button>
                </span>
              )}
              {filters.recruiter && (
                <span className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full">
                  Recruiter: {filters.recruiter}
                  <button
                    onClick={() => handleFilterChange('recruiter', '')}
                    className="ml-1 text-primary-600 hover:text-primary-800"
                  >
                    <Icon name="X" size={12} />
                  </button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterPanel;